/**
 * Canonical internal-member classification.
 *
 * This pure function is the single source of truth for the two member-level
 * analytics flags (see ADR-081 naming; person-property keys below). The SQL
 * mirror in `@galapagos/shared/db` must stay equivalent — a fixture-driven
 * drift-guard test pins the two implementations to each other.
 */

const PELAGO_EMAIL_DOMAIN = 'pelagohealth.com';
/**
 * Pelago's former domain. Staff and tester accounts created before the rename
 * still sign in with it, so it marks an internal user. It does not set
 * `isPelagoEmailDomain`, which keeps meaning the current staff domain.
 */
const LEGACY_PELAGO_EMAIL_DOMAIN = 'quitgenius.com';
const TEST_LOCAL_PART_MARKER = '+test';

/** Person-property keys as they appear in the analytics store. */
export const INTERNAL_MEMBER_PERSON_PROPERTY_KEYS = {
  isPelagoEmailDomain: 'is_pelago_email_domain',
  isInternalUser: 'is_internal_user',
} as const;

export interface InternalMemberClassification {
  isPelagoEmailDomain: boolean;
  isInternalUser: boolean;
}

/**
 * Normalization applied to curated-list emails before membership comparison.
 * Both sides of the comparison (list rows and the member email) must pass
 * through this so case and stray whitespace never defeat a match.
 */
export function normalizeInternalListEmail(email: string): string {
  return email.trim().toLowerCase();
}

function splitOnFinalAt(email: string): { localPart: string; domain: string } | undefined {
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return undefined;
  return { localPart: email.slice(0, at), domain: email.slice(at + 1) };
}

/**
 * Classify a member by email. `curatedEmails` must contain values already
 * passed through `normalizeInternalListEmail`.
 *
 * - `isPelagoEmailDomain`: the domain after the final `@` equals
 *   `pelagohealth.com` exactly (case-insensitive; subdomains and suffix
 *   look-alikes never match).
 * - `isInternalUser`: superset — Pelago domain, OR the former
 *   `quitgenius.com` domain (same exact-match rule: no subdomains, no suffix
 *   look-alikes), OR curated-list membership (trimmed, case-insensitive
 *   full-email equality), OR a local part carrying the `+test` marker
 *   (substring, case-insensitive; never sought in the domain).
 * - A null or empty email is never internal.
 */
export function classifyInternalMember(
  email: string | null | undefined,
  curatedEmails: ReadonlySet<string>,
): InternalMemberClassification {
  if (!email) return { isPelagoEmailDomain: false, isInternalUser: false };
  const normalized = normalizeInternalListEmail(email);
  if (normalized.length === 0) return { isPelagoEmailDomain: false, isInternalUser: false };

  const parts = splitOnFinalAt(normalized);
  const isPelagoEmailDomain = parts !== undefined && parts.domain === PELAGO_EMAIL_DOMAIN;
  const isLegacyPelagoEmailDomain =
    parts !== undefined && parts.domain === LEGACY_PELAGO_EMAIL_DOMAIN;
  const isOnCuratedList = curatedEmails.has(normalized);
  const hasTestLocalPart = parts !== undefined && parts.localPart.includes(TEST_LOCAL_PART_MARKER);

  return {
    isPelagoEmailDomain,
    isInternalUser:
      isPelagoEmailDomain || isLegacyPelagoEmailDomain || isOnCuratedList || hasTestLocalPart,
  };
}
